import type { Color } from '@disy/lib/colors';
import { assertNonNullable, requireNonNullable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { getSetting } from '@disy/workbook.lib/settings';

import { Select } from 'ui/select/select';

import type { GraphicalPropertyType } from 'cadenza/api-client/model/graphical-property-type.gen';
import { Command } from 'cadenza/api-client/workbook-commands';
import { createPickerAttributes } from 'cadenza/attribute-util';
import type { AttributeType } from 'cadenza/repository/model/attribute-type.gen';
import type { RootState } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import { AttributePicker } from 'cadenza/ui/attribute-picker/attribute-picker';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import { AttributeBundleSettingsKey } from 'cadenza/workbook/model/attribute-bundle-settings-key.gen';
import { getBaseAttributeId } from 'cadenza/workbook/model/attribute-id-util';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { ClassificationType } from 'cadenza/workbook/model/classification-type.gen';
import { getSupportedStandardClassificationTypes } from 'cadenza/workbook/model/classification-type.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { isAttributeToOneReachableFrom } from 'cadenza/workbook/state/workbook-reachability.state';
import { getVisibleAttributes } from 'cadenza/workbook/workbook-attribute-util';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getAttributeBundleSettings,
  getAttributeById,
  getAttributesFromBundles,
  getDataViewById,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { AttributeBundleValidator } from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator';
import { EditableNamePanel } from 'cadenza/workbook/workbook-panel/settings/editable-name-panel/editable-name-panel';
import { ClassificationTypeElement } from 'cadenza/workbook/workbook-panel/settings/map/advanced-data-layer-settings/classification-type-element/classification-type-element';
import { attributeBundleAccepts } from 'cadenza/workbook/workbook-panel/settings/map/settings-util/settings-util';

import i18n from './advanced-style-property-classification-settings.properties';

export type ClassificationStatus = 'UNCLASSIFIED' | 'CLASSIFIED';

export function getMandatoryClassificationActiveAttributeId(
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
): ActiveAttributeId {
  const classificationAttributeId = getClassificationActiveAttributeId(dataViewId, bundleName);
  assertNonNullable(classificationAttributeId);
  return classificationAttributeId;
}

export function getClassificationAttributeId(
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
): AttributeId | undefined {
  const activeAttributeId = getClassificationActiveAttributeId(dataViewId, bundleName);
  return activeAttributeId ? getBaseAttributeId(activeAttributeId) : undefined;
}

export function getAttributeClassificationName(dataViewId: DataViewId, bundleName: AttributeBundleName) {
  const attributeId = getClassificationActiveAttributeId(dataViewId, bundleName);
  if (attributeId) {
    const attribute = getAttributeById(getStore().getState(), attributeId);
    return attribute && getSetting<string>(attribute, 'CLASSIFICATION_NAME', attribute.printName);
  }
}

export function createSetClassificationAttributeCommands(
  currentAttributeBundleName: AttributeBundleName,
  nextAttributeBundleName: AttributeBundleName,
  dataViewId: DataViewId,
  attributeId?: AttributeId,
) {
  const commands = [];
  const currentAttributeId = getClassificationActiveAttributeId(dataViewId, currentAttributeBundleName);
  if (currentAttributeId) {
    commands.push(
      Command.REMOVE_ATTRIBUTE({
        dataViewId,
        attributeId: currentAttributeId,
      }),
    );
  }
  if (attributeId) {
    commands.push(
      Command.ADD_ATTRIBUTE({
        dataViewId,
        attributeBundleName: nextAttributeBundleName,
        attributeId,
      }),
    );
    if (!isBundleVisible(dataViewId, nextAttributeBundleName)) {
      commands.push(
        Command.CHANGE_ATTRIBUTE_BUNDLE_SETTINGS({
          dataViewId,
          attributeBundleName: nextAttributeBundleName,
          settings: {
            [AttributeBundleSettingsKey.toPersistenceKey('BUNDLE_VISIBLE')]: String(true),
          },
        }),
      );
    }
  }
  if (currentAttributeId && !attributeId) {
    // When reset to static then hide bundle
    commands.push(
      Command.CHANGE_ATTRIBUTE_BUNDLE_SETTINGS({
        dataViewId,
        attributeBundleName: nextAttributeBundleName,
        settings: {
          [AttributeBundleSettingsKey.toPersistenceKey('BUNDLE_VISIBLE')]: String(false),
        },
      }),
    );
  }
  return commands;
}

export async function setClassificationAttribute(
  currentAttributeBundleName: AttributeBundleName,
  nextAttributeBundleName: AttributeBundleName,
  dataViewId: DataViewId,
  attributeId?: AttributeId,
) {
  const commands = createSetClassificationAttributeCommands(
    currentAttributeBundleName,
    nextAttributeBundleName,
    dataViewId,
    attributeId,
  );
  if (commands.length) {
    await getStore().dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands })));
  }
  return commands;
}

export function getAllPossibleClassificationAttributes(dataViewId: DataViewId, bundleName: AttributeBundleName) {
  return getVisibleAttributes().filter((attribute) =>
    isAttributeSupportedForClassification(attribute, dataViewId, bundleName),
  );
}

function isAttributeSupportedForClassification(
  attribute: WorkbookAttributeDto,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) {
  const dataView = getDataViewById(getStore().getState(), dataViewId);
  const bundleDefinition = dataView.attributeBundleDefinitions[bundleName]!;
  const validator = new AttributeBundleValidator({
    dataViewId,
    attributesProvider: () => getAttributesFromBundles(getStore().getState(), dataViewId, bundleName),
  });
  return attributeBundleAccepts(bundleDefinition, validator, attribute);
}

function isBundleVisible(dataViewId: DataViewId, bundleName: AttributeBundleName) {
  const attributeBundleSettings = getAttributeBundleSettings(getStore().getState(), dataViewId, bundleName);
  return attributeBundleSettings?.['bundle.visible'];
}

export function getClassificationActiveAttributeId(
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
  state: RootState = getStore().getState(),
): ActiveAttributeId | undefined {
  const dataView = getDataViewById(state, dataViewId);
  const classificationAttributes = dataView.attributes[bundleName];
  return classificationAttributes?.[0];
}

export function getRequiredClassificationActiveAttributeId(
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
): ActiveAttributeId {
  return requireNonNullable(getClassificationActiveAttributeId(dataViewId, bundleName));
}

export function getAttributeSingleColor(attributeId: ActiveAttributeId) {
  const attribute = getAttributeById(getStore().getState(), attributeId);
  return getSetting<Color>(attribute, 'SINGLE_COLOR');
}

export function getAttributeDefaultColor(attributeId: ActiveAttributeId) {
  const attribute = getAttributeById(getStore().getState(), attributeId);
  return attribute.defaultSingleColor;
}

export function getAttributeSingleOrDefaultColor(attribute: WorkbookAttributeDto) {
  return getSetting<Color>(attribute, 'SINGLE_COLOR') ?? attribute.defaultSingleColor;
}

export function createAttributePicker(
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
  supportsOnlyMeasures: boolean,
  onChange: (attr: AttributeId) => void,
) {
  const attributes = getAllPossibleClassificationAttributes(dataViewId, bundleName);
  const data = attributes.flatMap(createPickerAttributes);
  const attributePicker = new AttributePicker<AttributeId>(data, {
    required: true,
    withRemoveButton: false,
    invisibleAttributesRendering: {
      createInvisibleAttribute: (attributeId) => getAttributeById(getStore().getState(), attributeId),
      showInvisibleAttribute: () => true,
    },
  });
  if (supportsOnlyMeasures) {
    attributePicker.setAttribute('data-validation-hint', i18n('attributePicker.hint'));
  }

  const classificationAttributeId = getClassificationAttributeId(dataViewId, bundleName);
  attributePicker.value = classificationAttributeId ? [classificationAttributeId] : [];

  on(attributePicker, 'change', () => {
    const attributeId = attributePicker.selection as AttributeId;
    onChange(attributeId);
  });

  return attributePicker;
}

export function createClassificationNamePanel(
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
  attributePicker: AttributePicker<AttributeId>,
) {
  const panel = new EditableNamePanel(i18n('classificationName.label'), { required: false, showAutoToggle: true });
  panel.classList.add('form-group-s');

  on(panel, 'change:name', () => setClassificationNameInStore(panel.name, dataViewId, bundleName));
  on(panel, 'change:auto', async () => {
    if (panel.isAutoEnabled) {
      await setAutoClassificationNameInStore(dataViewId, bundleName);
      const printName = attributePicker?.selectedAttributes[0]?.printName;
      const classificationName = getAttributeClassificationName(dataViewId, bundleName);
      updateClassificationNamePanel(panel, printName, classificationName);
    } else {
      setClassificationNameInStore(panel.name, dataViewId, bundleName);
    }
  });
  return panel;
}

export function createClassificationTypeElement({
  dataViewId,
  bundleName,
  selectedAttribute,
  geometryAttributeId,
  onlySupportedClassification,
  graphicalPropertyType,
}: {
  dataViewId: DataViewId;
  bundleName: AttributeBundleName;
  selectedAttribute: WorkbookAttributeDto;
  geometryAttributeId: ActiveAttributeId;
  onlySupportedClassification?: ClassificationType;
  graphicalPropertyType: GraphicalPropertyType;
}) {
  if (selectedAttribute.category === 'HIERARCHY') {
    return undefined;
  }
  const isMeasure = selectedAttribute.category === 'MEASURE';
  let isTo1GeometryLinkedMeasure = false;
  const classificationActiveAttributeId = getMandatoryClassificationActiveAttributeId(dataViewId, bundleName);
  const classificationAttr = getAttributeById(getStore().getState(), classificationActiveAttributeId);
  if (isMeasure && measureTypeAllowsUniqueValueClassification(selectedAttribute.type)) {
    const targetAttribute = getAttributeById(getStore().getState(), geometryAttributeId);
    isTo1GeometryLinkedMeasure = isAttributeToOneReachableFrom(
      getStore().getState(),
      classificationAttr,
      targetAttribute,
    );
  }
  const defaultValue = getDefaultClassificationType(selectedAttribute, onlySupportedClassification);
  const value = getSetting(classificationAttr, AttributeModificationKey.CLASSIFICATION_TYPE, defaultValue);
  const supportedClassificationTypes = getSupportedStandardClassificationTypes(graphicalPropertyType);
  const isReadOnly = !isTo1GeometryLinkedMeasure || supportedClassificationTypes.length < 2;

  const classificationType = new ClassificationTypeElement();
  classificationType.value = value;
  classificationType.readOnly = isReadOnly;
  on(classificationType, 'change', () => {
    setClassificationType(
      dataViewId,
      getMandatoryClassificationActiveAttributeId(dataViewId, bundleName),
      classificationType.value,
      isMeasure,
    );
  });
  return classificationType;
}

export function getDefaultClassificationType(
  selectedAttribute: WorkbookAttributeDto,
  onlySupportedClassification?: ClassificationType,
) {
  const isMeasure = selectedAttribute.category === 'MEASURE';
  return onlySupportedClassification ?? (isMeasure ? 'INTERVAL' : 'UNIQUE_VALUES');
}

export function updateClassificationNamePanel(
  classificationNamePanel: EditableNamePanel,
  attributePrintName?: string,
  classificationName: string = '',
) {
  classificationNamePanel.update({
    name: classificationName,
    defaultName: classificationName === attributePrintName ? undefined : attributePrintName,
  });
}

function setAutoClassificationNameInStore(dataViewId: DataViewId, bundleName: AttributeBundleName) {
  const attributeId = getMandatoryClassificationActiveAttributeId(dataViewId, bundleName);
  return getStore().dispatch(
    executeCommand(
      Command.REMOVE_DATA_VIEW_ATTRIBUTE_MODIFICATION({
        dataViewId,
        attributeId,
        modificationType: 'CLASSIFICATION_NAME',
      }),
    ),
  );
}

function setClassificationNameInStore(
  classificationName: string | undefined,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) {
  const attributeId = getMandatoryClassificationActiveAttributeId(dataViewId, bundleName);
  return getStore().dispatch(
    executeCommand(
      Command.SET_DATA_VIEW_ATTRIBUTE_MODIFICATION({
        attributeId,
        dataViewId,
        modificationType: 'CLASSIFICATION_NAME',
        value: String(classificationName),
      }),
    ),
  );
}

function setClassificationType(
  dataViewId: DataViewId,
  attributeId: ActiveAttributeId,
  classificationType: ClassificationType,
  isMeasure: boolean,
) {
  const setClassificationTypeCommand = Command.SET_DATA_VIEW_ATTRIBUTE_MODIFICATION({
    dataViewId,
    attributeId,
    modificationType: 'CLASSIFICATION_TYPE',
    value: classificationType,
  });
  const commands = [setClassificationTypeCommand];
  if (isMeasure && classificationType === 'UNIQUE_VALUES') {
    // for measure and CLASSIFICATION_TYPE === 'UNIQUE_VALUES' the only possible value for 'CLASSIFICATION_MODE' is 'CUSTOM'
    commands.push(
      Command.SET_DATA_VIEW_ATTRIBUTE_MODIFICATION({
        dataViewId,
        attributeId,
        modificationType: 'CLASSIFICATION_MODE',
        value: 'CUSTOM',
      }),
    );
  }
  return getStore().dispatch(
    executeCommand(
      Command.COMPOSITE_COMMAND({
        commands,
      }),
    ),
  );
}

export function measureTypeAllowsUniqueValueClassification(type: AttributeType) {
  return ['INT', 'LONG'].includes(type);
}

export function createSelectItem(
  classificationStatus: ClassificationStatus,
  currentlySelectedClassificationStatus: ClassificationStatus,
) {
  return {
    id: classificationStatus,
    data: classificationStatus,
    text: i18n(`classificationStatus.<${classificationStatus}>`),
    selected: classificationStatus === currentlySelectedClassificationStatus,
  };
}

export function createClassificationStatusSelect(
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
  onChange: (type: ClassificationStatus, e: Event) => void,
) {
  const classificationAttributeId = getClassificationAttributeId(dataViewId, bundleName);
  const currentlySelectedClassificationStatus = classificationAttributeId ? 'CLASSIFIED' : 'UNCLASSIFIED';
  const classificationStatusSelect = new Select<ClassificationStatus>({
    data: [
      createSelectItem('UNCLASSIFIED', currentlySelectedClassificationStatus),
      createSelectItem('CLASSIFIED', currentlySelectedClassificationStatus),
    ],
  });

  on(classificationStatusSelect, 'change', (event) => {
    onChange(requireNonNullable(classificationStatusSelect.selection[0].data), event);
  });

  return classificationStatusSelect;
}
